import { initGiftMessageToggle, fetchCartData } from 'cart-utils';
import { checkProductQuantityLimit } from 'product-quantity-limit';
import { trapFocus, removeTrapFocus } from 'dom-utils';
import { initBogoGift } from 'bogo-gift';

window.BSS = window.BSS || {};
BSS.Components = BSS.Components || {};

/**
 * Defines the `BSS.Components.QuickCart` module which encapsulates functionalities related to the quick cart feature
 * This module manages interactions such as adding items to the cart, replacing cart contents dynamically, and other
 * cart-related operations.
 * @module BSS.Components.QuickCart
 * @returns {Object} An object exposing public methods for the quick cart, specifically `addItemToCart` and `replaceCartContents`.
 */
BSS.Components.QuickCart = (function () {
  const spinnerWidget =
    '<i class="cart-loading fa fa-spin fa-lg fa-spinner"></i>';
  const buttonTextResetTime = 3000;

  async function refreshCartAfterGiftChange() {
    const cart = await fetchCartData();
    updateCartItemCounts(cart?.item_count ?? 0);
    await replaceCartContents(getScrollPosition() ?? 0, cart, true);
  }

  function initializeBogoGift() {
    return initBogoGift({
      root: document.querySelector('.quick-cart [data-bogo-gift]'),
      onCartChanged: refreshCartAfterGiftChange
    });
  }

  /**
   * Opens the cart drawer on the page, adds relevant event listeners, initializes Rebuy if available,
   * and sets the aria-hidden attribute for cart items to false, making them visible to screen readers.
   */
  async function openCartDrawer(refreshContents = true) {
    document.body.classList.add('quick-cart--active');
    let drawerInitialized = !refreshContents;
    if (refreshContents) {
      try {
        const cart = await fetchCartData();
        updateCartItemCounts(cart?.item_count ?? 0);
        await replaceCartContents(0, cart);
        drawerInitialized = true;
      } catch (error) {
        console.error('Unable to refresh the cart drawer:', error);
      }
    }
    if (!drawerInitialized) {
      addCartDrawerListeners();
      void initializeBogoGift();
    }
    // Initialize cart upsells if cart has items and CartUpsells is defined
    if (
      document.querySelector('.quick-cart-item') &&
      typeof BSS.Components?.CartUpsells !== 'undefined'
    ) {
      BSS.Components.CartUpsells.init();
    }
    dataLayer.push({ event: 'quick_cart_view' });
    const cartInner = document.querySelector('.quick-cart__inner');
    cartInner?.setAttribute('aria-hidden', 'false');
    cartInner?.removeAttribute('inert');
    const closeBtn = document.querySelector('#quick-cart-close');
    trapFocus(
      document.querySelector('.quick-cart__inner'),
      closeBtn ?? undefined
    );
  }

  /**
   * Closes the cart drawer on the page and sets the aria-hidden attribute for cart items to true,
   * making them invisible to screen readers.
   */
  function closeCartDrawer() {
    document.body.classList.remove('quick-cart--active');
    const cartInner = document.querySelector('.quick-cart__inner');
    cartInner?.setAttribute('aria-hidden', 'true');
    cartInner?.setAttribute('inert', '');
    removeTrapFocus(document.querySelector('.cart-button'));
  }

  /**
   * Updates the item count displayed on all elements with the class name 'cart-item-count'. Also toggles
   * the visibility of the cart and count elements based on the provided count.
   * @param {number} count - The new count of items to be displayed.
   */
  function updateCartItemCounts(count) {
    document.querySelectorAll('.cart-item-count').forEach(el => {
      // Update only the text node so child elements (e.g. visually-hidden) are preserved
      const textNode = [...el.childNodes].find(
        n => n.nodeType === Node.TEXT_NODE
      );
      if (textNode) textNode.nodeValue = count;
      if (count > 0) {
        el.classList.remove('hide');
        document.querySelector('.cart-icon')?.classList.add('cart--active');
      } else {
        el.classList.add('hide');
        document.querySelector('.cart-icon')?.classList.remove('cart--active');
      }
    });

    document.querySelectorAll('.cart-item-count-label').forEach(el => {
      el.textContent = `Cart, ${count} ${count === 1 ? 'item' : 'items'}`;
    });
  }

  /**
   * Temporarily replaces the inner text of a given element with a loading spinner, then restores
   * the original text after a predefined duration (`buttonTextResetTime`).
   * @param {HTMLElement} element - The element to update with a loading state.
   */
  function updateCtaLoadingState(element) {
    if (!element) return;
    const ctaText = element.innerText;
    element.innerHTML = spinnerWidget;

    setTimeout(() => {
      element.innerText = ctaText;
    }, buttonTextResetTime);
  }

  /**
   * Adds an item to the cart and refreshes the cart UI.
   * Also handles showing and hiding the product modal based on the add-to-cart operation.
   * @param {Object} data Product data for the item being added.
   * @param {HTMLElement} element The button element that initiated the add-to-cart action.
   */
  async function addItemToCart(data, element) {
    updateCtaLoadingState(element);
    const requestData = {
      items: [{ id: data?.id, quantity: 1, properties: data?.properties || {} }]
    };
    await fetch('/cart/add.js', {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        'X-Product-Card': '1'
      },
      body: JSON.stringify(requestData)
    });

    const cart = await fetchCartData();
    updateCartItemCounts(cart.item_count);

    // Hide the mobile modal if present
    const productModal = document.getElementById('product-modal');
    if (productModal && productModal.getAttribute('active') === 'true') {
      productModal.setAttribute('active', 'false');
    }

    // Ensure cart contents are fully replaced before opening the drawer
    await replaceCartContents(0, cart);
    await openCartDrawer(false);
    dataLayer.push({ event: 'quick_add_to_cart' });

    // Trigger CDP add-to-cart event
    sendCDPAddToCartEvent(cart);
  }

  /**
   * Replaces the contents of the cart drawer with updated cart data.
   * @param {number} scrollPosition The current scroll position to restore after update.
   * @param {Object} cart The cart data used to update the drawer.
   * @param {boolean} initializeUpsells Whether to initialize cart upsells after replacing contents.
   */
  async function replaceCartContents(
    scrollPosition,
    cart,
    initializeUpsells = false
  ) {
    const res = await fetch('/?section_id=quick-cart');
    const text = await res.text();
    const html = document.createElement('div');
    html.innerHTML = text;

    const newBox = html.querySelector('.quick-cart')?.innerHTML;
    const quickCartElement = document.querySelector('.quick-cart');

    if (!newBox || !quickCartElement) {
      console.error('Failed to find quick-cart element');
      return;
    }

    quickCartElement.innerHTML = newBox;
    const freshCartItems = quickCartElement.querySelector('.quick-cart__inner');
    if (freshCartItems) {
      freshCartItems.scrollTop = scrollPosition;
      // Restore interactivity on the newly rendered inner (template defaults to inert + aria-hidden)
      freshCartItems?.setAttribute('aria-hidden', 'false');
      freshCartItems?.removeAttribute('inert');
    }

    addCartDrawerListeners();
    await initializeBogoGift();
    quickCartElement.classList.toggle('empty', cart?.item_count === 0);

    // Initialize cart upsells if requested and cart is not empty
    if (cart?.item_count > 0) {
      if (
        initializeUpsells &&
        typeof BSS.Components.CartUpsells !== 'undefined'
      ) {
        BSS.Components.CartUpsells.init();
      }
      //covert code for cartUrgency Test trigger
      window.runExperimentCartUrgency = 'true';
      window._conv_q = _conv_q || [];
      _conv_q.push(['executeExperiment', '1004216465']);
    }
    // Check if product has reached quantity limit
    checkProductQuantityLimit();
  }

  /**
   * cart icon listener
   */
  document.querySelector('.cart-button')?.addEventListener('click', e => {
    void openCartDrawer();
  });

  /**
   * product page form listener
   */
  document.querySelectorAll('form[action="/cart/add"]').forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();

      const ctaBtn = form.querySelector('button#add-to-cart');
      try {
        updateCtaLoadingState(ctaBtn);
        const response = await fetch('/cart/add.js', {
          method: 'post',
          body: new FormData(form)
        });

        if (!response.ok) {
          throw new Error('Error adding item to cart: ' + response.statusText);
        }
        const cart = await fetchCartData();
        await updateCartItemCounts(cart?.item_count ?? 0);
        await replaceCartContents(0, cart);
        await openCartDrawer(false);
        sendCDPAddToCartEvent(cart);
      } catch (error) {
        console.error('Error:', error);
      }
    });
  });

  /**
   * bundle products page form listener
   */
  document.querySelectorAll('form[action="/cart/add/bundle"]').forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const data = {
        items: []
      };
      const bundleProductId = form.getAttribute('data-productid');
      const selectedOptions = document.querySelectorAll(
        '.bundle-product-variant-id'
      );
      const bundleQuantity =
        Number(document.getElementById('bundle-product-quantity').value) || 1;
      const bundleDiscount = Number(
        document.getElementById('bundle-product-discount').value
      );

      selectedOptions.forEach(option => {
        const variantId = Number(option.value);
        const lineItem = {
          id: variantId,
          quantity: 1 * bundleQuantity,
          properties: {
            _bundle_product_id: bundleProductId
          }
        };
        if (bundleDiscount) {
          lineItem.properties['_bundle_product_discount'] = bundleDiscount;
        }
        // reverse the items order to match cart products order with pdp products order
        data.items.unshift(lineItem);
      });
      const ctaBtn = form.querySelector('button#add-to-cart');
      try {
        updateCtaLoadingState(ctaBtn);
        const response = await fetch('/cart/add.js', {
          method: 'post',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Quick-Cart': '1'
          },
          body: JSON.stringify(data)
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const cart = await fetchCartData();
        await updateCartItemCounts(cart.item_count);
        await replaceCartContents(0, cart);
        await openCartDrawer(false);
        sendCDPAddToCartEvent(cart);
      } catch (error) {
        console.error('Error:', error);
      }
    });
  });

  /**
   * Retrieves the current vertical scroll position of the '.quick-cart__inner' element.
   * This can be used to save the scroll position before updating the cart's contents, allowing for restoration afterward.
   * @returns {number|null} The vertical scroll position of the '.quick-cart__inner' element, or null if the element is not found.
   */
  function getScrollPosition() {
    const el = document.querySelector('.quick-cart__inner');
    if (!el) return;
    return el.scrollTop;
  }

  /**
   * Adds the event listeners for the cart drawer UI interactions that replaceCartContents() removes.
   */
  function addCartDrawerListeners() {
    // Close drawer
    document
      .querySelectorAll('#quick-cart-close, .quick-cart__overlay')
      .forEach(el => {
        el.addEventListener('click', () => {
          closeCartDrawer();
        });
      });
    // prevent bubbling for the overlay
    document
      .querySelector('.quick-cart__inner')
      ?.addEventListener('click', e => {
        e.stopPropagation();
      });

    // Update item quantities
    document
      .querySelectorAll('.quick-cart .quantity button')
      .forEach(button => {
        button.addEventListener('click', async e => {
          e.preventDefault();
          // Get line item key
          const cartItem = button.closest('.quick-cart-item');
          const key = cartItem.getAttribute('data-line-item-key');
          // Check for bundle item
          const bundleId = cartItem.getAttribute('data-bundle-id');
          // Get new quantity
          const quantitySelector = button.parentElement.querySelector('input');
          const currentQuantity = Number(quantitySelector.value);
          const isUp = button.classList.contains(
            'quick-cart-quantity-selector-plus'
          );
          const newQuantity = isUp ? currentQuantity + 1 : currentQuantity - 1;

          const updates = { [key]: newQuantity };

          if (bundleId) {
            document
              .querySelectorAll(
                `.quick-cart-item[data-bundle-id="${bundleId}"]`
              )
              .forEach(bundleItem => {
                const bundleKey = bundleItem.getAttribute('data-line-item-key');
                updates[bundleKey] = newQuantity;
              });
          }
          const response = await fetch('/cart/update.js', {
            method: 'post',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'X-Quick-Cart': '1'
            },
            body: JSON.stringify({ updates })
          });
          const cart = await response.json();

          await updateCartItemCounts(cart.item_count);
          await replaceCartContents(getScrollPosition(), cart, true);
        });
      });

    // Remove an item from the cart
    document.querySelectorAll('.remove-item').forEach(remove => {
      remove.addEventListener('click', async e => {
        e.preventDefault();

        remove.innerHTML = spinnerWidget;
        const item = remove.closest('.quick-cart-item');
        const key = item.getAttribute('data-line-item-key');
        // Check for bundle item
        const bundleId = item.getAttribute('data-bundle-id');

        if (bundleId) {
          const updates = {};
          document
            .querySelectorAll(`.quick-cart-item[data-bundle-id="${bundleId}"]`)
            .forEach(bundleItem => {
              const bundleKey = bundleItem.getAttribute('data-line-item-key');
              updates[bundleKey] = 0;
            });

          const response = await fetch('/cart/update.js', {
            method: 'post',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'X-Quick-Cart': '1'
            },
            body: JSON.stringify({ updates })
          });

          const cart = await response.json();
          await updateCartItemCounts(cart.item_count);
          await replaceCartContents(getScrollPosition(), cart, true);
        } else {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Quick-Cart': '1'
            },
            body: JSON.stringify({ id: key, quantity: 0 })
          });
          const cart = await response.json();
          await updateCartItemCounts(cart.item_count);
          await replaceCartContents(getScrollPosition(), cart, true);
        }
      });
      remove.addEventListener('keydown', async e => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();

          remove.innerHTML = spinnerWidget;
          const item = remove.closest('.quick-cart-item');
          const key = item.getAttribute('data-line-item-key');
          // Check for bundle item
          const bundleId = item.getAttribute('data-bundle-id');

          if (bundleId) {
            const updates = {};
            document
              .querySelectorAll(
                `.quick-cart-item[data-bundle-id="${bundleId}"]`
              )
              .forEach(bundleItem => {
                const bundleKey = bundleItem.getAttribute('data-line-item-key');
                updates[bundleKey] = 0;
              });

            const response = await fetch('/cart/update.js', {
              method: 'post',
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-Quick-Cart': '1'
              },
              body: JSON.stringify({ updates })
            });

            const cart = await response.json();
            await updateCartItemCounts(cart.item_count);
            await replaceCartContents(getScrollPosition(), cart, true);
          } else {
            const response = await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Quick-Cart': '1'
              },
              body: JSON.stringify({ id: key, quantity: 0 })
            });
            const cart = await response.json();
            await updateCartItemCounts(cart.item_count);
            await replaceCartContents(getScrollPosition(), cart, true);
          }
        }
      });
    });

    // set the cart attributes for CDP
    const anonymousIdCartInput = document.getElementById('cdp-anon-id');
    const barstoolIdCartInput = document.getElementById('cdp-barstool-id');
    if (
      (anonymousIdCartInput && anonymousIdCartInput.value === '') ||
      (barstoolIdCartInput && barstoolIdCartInput.value === '')
    ) {
      setCDPCartAttributes();
    }
    // init the gift message listener
    initGiftMessageToggle();
  }

  return {
    addItemToCart,
    replaceCartContents,
    openCartDrawer,
    updateCartItemCounts
  };
})();
